import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '../components/ui/card';
import { MapPin, ArrowRight } from 'lucide-react';

const Ciudades = () => {
  const comunidades = [
    {
      nombre: 'Comunidad de Madrid',
      color: 'bg-nubia-green-primary',
      ciudades: [
        { nombre: 'Madrid', slug: 'madrid', habitantes: '3.3M' },
        { nombre: 'Alcalá de Henares', slug: 'alcala-de-henares', habitantes: '195K' },
        { nombre: 'Getafe', slug: 'getafe', habitantes: '183K' },
        { nombre: 'Leganés', slug: 'leganes', habitantes: '190K' },
        { nombre: 'Alcorcón', slug: 'alcorcon', habitantes: '170K' },
        { nombre: 'Móstoles', slug: 'mostoles', habitantes: '209K' },
        { nombre: 'Fuenlabrada', slug: 'fuenlabrada', habitantes: '193K' },
        { nombre: 'Parla', slug: 'parla', habitantes: '130K' },
        { nombre: 'Pozuelo de Alarcón', slug: 'pozuelo-de-alarcon', habitantes: '86K' },
        { nombre: 'Majadahonda', slug: 'majadahonda', habitantes: '71K' }
      ]
    },
    {
      nombre: 'Comunidad Valenciana',
      color: 'bg-nubia-green-support',
      ciudades: [
        { nombre: 'Valencia', slug: 'valencia', habitantes: '800K' },
        { nombre: 'Alicante', slug: 'alicante', habitantes: '337K' },
        { nombre: 'Castellón', slug: 'castellon', habitantes: '174K' },
        { nombre: 'Torrent', slug: 'torrent', habitantes: '83K' },
        { nombre: 'Gandía', slug: 'gandia', habitantes: '75K' },
        { nombre: 'Elche', slug: 'elche', habitantes: '234K' },
        { nombre: 'Benidorm', slug: 'benidorm', habitantes: '70K' },
        { nombre: 'Sagunto', slug: 'sagunto', habitantes: '66K' },
        { nombre: 'Paterna', slug: 'paterna', habitantes: '70K' },
        { nombre: 'Burjassot', slug: 'burjassot', habitantes: '38K' }
      ]
    },
    {
      nombre: 'Extremadura',
      color: 'bg-nubia-green-accent',
      ciudades: [
        { nombre: 'Badajoz', slug: 'badajoz', habitantes: '151K' },
        { nombre: 'Cáceres', slug: 'caceres', habitantes: '96K' },
        { nombre: 'Mérida', slug: 'merida', habitantes: '60K' },
        { nombre: 'Plasencia', slug: 'plasencia', habitantes: '41K' },
        { nombre: 'Almendralejo', slug: 'almendralejo', habitantes: '35K' },
        { nombre: 'Don Benito', slug: 'don-benito', habitantes: '37K' },
        { nombre: 'Villanueva de la Serena', slug: 'villanueva-de-la-serena', habitantes: '26K' },
        { nombre: 'Navalmoral de la Mata', slug: 'navalmoral-de-la-mata', habitantes: '17K' }
      ]
    }
  ];

  const totalCiudades = comunidades.reduce((total, com) => total + com.ciudades.length, 0);

  return (
    <>
      <Helmet>
        <title>Ciudades - Instalación de Placas Solares en España | Nubia Solar</title>
        <meta name="description" content="Descubre todas las ciudades donde Nubia Solar instala placas solares: Madrid, Valencia y Extremadura. 28 ciudades con servicio profesional de energía solar." />
        <meta name="keywords" content="instalacion placas solares madrid, placas solares valencia, energia solar extremadura, ciudades servicio solar" />
      </Helmet>

      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="py-20 bg-nubia-gray-light">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center gap-3 mb-6">
                <MapPin className="w-12 h-12 text-nubia-gold-primary" />
                <h1 className="text-5xl md:text-6xl font-bold text-nubia-green-primary">
                  Nuestras Ciudades
                </h1>
              </div>
              <p className="text-xl text-nubia-text-dark/80 mb-4">
                Instalamos placas solares en <strong>{totalCiudades} ciudades</strong> de España
              </p>
              <p className="text-lg text-nubia-text-dark/70">
                Comunidad de Madrid • Comunidad Valenciana • Extremadura
              </p>
            </div>
          </div>
        </section>

        {/* Cities by Community */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-7xl mx-auto">
              {comunidades.map((comunidad, index) => (
                <div key={index} className="mb-20 last:mb-0">
                  {/* Community Header */}
                  <div className="mb-10">
                    <div className={`inline-block px-6 py-3 rounded-full ${comunidad.color} text-white mb-4`}>
                      <span className="font-semibold text-lg">{comunidad.nombre}</span>
                    </div>
                    <p className="text-nubia-text-dark/70 text-lg">
                      {comunidad.ciudades.length} ciudades disponibles
                    </p>
                  </div>

                  {/* Cities Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {comunidad.ciudades.map((ciudad) => (
                      <Link 
                        key={ciudad.slug} 
                        to={`/placas-solares/${ciudad.slug}`}
                        className="group"
                      >
                        <Card className="h-full nubia-shadow border-2 border-transparent hover:border-nubia-gold-primary hover:scale-105 transition-all duration-300 cursor-pointer">
                          <CardContent className="p-6">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <h3 className="text-xl font-bold text-nubia-green-primary mb-1 group-hover:text-nubia-gold-primary transition-colors">
                                  {ciudad.nombre}
                                </h3>
                                <p className="text-sm text-nubia-text-dark/60">
                                  {ciudad.habitantes} habitantes
                                </p>
                              </div>
                              <ArrowRight className="w-5 h-5 text-nubia-gold-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                            
                            <div className="flex items-center gap-2 text-sm text-nubia-green-support mt-4">
                              <MapPin className="w-4 h-4" />
                              <span className="font-medium">Ver instalaciones</span>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 nubia-gradient text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              ¿Tu Ciudad No Aparece?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
              Estamos en continua expansión. Contacta con nosotros para consultar disponibilidad en tu zona.
            </p>
            <Link 
              to="/contacto"
              className="inline-block bg-nubia-gold-primary hover:bg-nubia-gold-primary/90 text-nubia-green-primary font-semibold px-10 py-4 rounded-lg shadow-xl transition-all"
            >
              Contactar Ahora
              <ArrowRight className="inline-block ml-2 w-5 h-5" />
            </Link>
          </div>
        </section>

        {/* Info Section */}
        <section className="py-16 bg-nubia-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <Card className="nubia-shadow border-0">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-nubia-green-primary mb-4">
                    ¿Por Qué Elegir Nubia Solar?
                  </h3>
                  <div className="space-y-3 text-nubia-text-dark/80">
                    <p className="flex items-start gap-3">
                      <span className="text-nubia-gold-primary font-bold">✓</span>
                      <span><strong>Presencia local:</strong> Conocemos las características y normativas de cada ciudad</span>
                    </p>
                    <p className="flex items-start gap-3">
                      <span className="text-nubia-gold-primary font-bold">✓</span>
                      <span><strong>Instalación rápida:</strong> Equipo técnico en tu zona, instalación en 1-2 días</span>
                    </p>
                    <p className="flex items-start gap-3">
                      <span className="text-nubia-gold-primary font-bold">✓</span>
                      <span><strong>Tramitación de ayudas:</strong> Gestionamos todas las subvenciones locales sin coste extra</span>
                    </p>
                    <p className="flex items-start gap-3">
                      <span className="text-nubia-gold-primary font-bold">✓</span>
                      <span><strong>Garantía total:</strong> 25 años de garantía en todas nuestras instalaciones</span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Ciudades;
